import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useClickAway } from 'react-use';
import { IconKebap } from '../../icons';
import { Tooltip } from '../Tooltip/Tooltip';

interface KebapMenuProps {
  children: React.ReactNode;
  testId?: string;
}

interface MenuItemProps {
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  testId?: string;
  tooltip?: React.ReactElement | string;
}

const KebabButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  position: relative;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }
`;

const StyledMenu = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  top: 100%;
  right: 0;
  width: fit-content;
  max-height: 164px;
  overflow-y: auto;
  background-color: ${({ theme }) =>
    theme.color ? theme.color('#FFFFFF') : '#FFFFFF'};
  border: 1px solid
    ${({ theme }) => (theme.color ? theme.color('#F6F6F6') : '#F6F6F6')};
  border-radius: 8px;
  z-index: 20;
  box-shadow: -8px 9px 17px -4px rgba(0, 0, 0, 0.14);
  margin-top: 5px;
`;
const StyledIconContainer = styled.div<{ readonly disabled?: boolean }>`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  ${({ disabled }) =>
    disabled &&
    css`
      & > svg {
        opacity: 0.4;
      }
    `}
`;

const StyledMenuItem = styled.div<{ readonly disabled?: boolean }>`
  cursor: pointer;
  white-space: nowrap;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  user-select: none;

  &:hover {
    background-color: ${({ theme }) =>
      theme.color ? theme.color('#F2F0F0') : '#F2F0F0'};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      color: #ababab;

      &:hover {
        background-color: initial;
      }
    `}
`;

export const MenuItem = ({
  children,
  onClick,
  disabled = false,
  icon = null,
  testId = 'menu-item',
  setIsOpen,
  tooltip,
}: MenuItemProps) => {
  return (
    <StyledMenuItem
      data-testid={testId}
      disabled={disabled}
      onClick={
        disabled
          ? undefined
          : () => {
              onClick?.();
              if (setIsOpen) setIsOpen(false); // Close menu after clicking
            }
      }
    >
      <StyledIconContainer disabled={disabled}>
        {icon && icon}
      </StyledIconContainer>
      {tooltip && <Tooltip title={tooltip}>{children}</Tooltip>}
      {!tooltip && children}
    </StyledMenuItem>
  );
};

export const KebapMenu = ({ testId, children }: KebapMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useClickAway(menuRef, () => setIsOpen(false));

  return (
    <div
      ref={menuRef}
      data-testid={testId ?? 'kebab-menu'}
      style={{ position: 'relative' }}
    >
      <KebabButton onClick={() => setIsOpen(!isOpen)}>
        <IconKebap />
      </KebabButton>

      <StyledMenu isOpen={isOpen}>
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(
                child as React.ReactElement<{
                  setIsOpen: (isOpen: boolean) => void;
                }>,
                { setIsOpen },
              )
            : child,
        )}
      </StyledMenu>
    </div>
  );
};
